import { api, ApiError } from './api';
import {
  ArchiveCategoryId,
  ArchiveDependenciesResponse,
  ArchiveDependencyItem,
  ArchiveDraftResponse,
  ArchiveEvent,
  ArchiveEventPayload,
  ArchiveEventResponse,
  ArchivePerson,
  ArchivePersonResponse,
  ArchiveReplacement,
  ArchiveSelectablePeopleResponse,
  RaciRoleLabel,
  TerminationReason
} from '../types/personArchive';

/**
 * Archive Person journey API.
 *
 * Resolving a dependency is deliberately two calls: the domain endpoint that
 * really performs the change (asset return, system revocation, procedure
 * transfer, manager/RACI/desk/yard handover), then the journey's event endpoint
 * which *verifies* the change landed and composes the Change-history line. The
 * server never trusts client prose, so the ids sent here matter more than the
 * labels.
 */

type PartyEntry = { id: number; type: string };
type PartySlot = PartyEntry | PartyEntry[] | null;

interface YardMemberRow {
  id: number;
  user?: { id?: number | string };
  userId?: number;
  yardRole?: string;
}

interface ActivityRaciResponse {
  activity?: { raci?: unknown };
  raci?: unknown;
}

/** `in`-narrowed read of one stored RACI party, tolerant of junk in the jsonb. */
function toPartyEntry(entry: unknown): PartyEntry | null {
  if (!entry || typeof entry !== 'object' || !('id' in entry)) return null;

  const id = Number(entry.id);
  if (!Number.isFinite(id)) return null;

  const type = 'type' in entry && typeof entry.type === 'string' ? entry.type : 'User';
  return { id, type };
}

/** Strip hydrated extras so only clean `{id, type}` pairs reach the jsonb column. */
function normalizeRaciSlot(slot: unknown): PartySlot {
  if (!slot) return null;

  if (Array.isArray(slot)) {
    return slot.map(toPartyEntry).filter((entry): entry is PartyEntry => entry !== null);
  }

  return toPartyEntry(slot);
}

async function listYardMembers(yardId: number): Promise<YardMemberRow[]> {
  const response = await api.get<{ members?: YardMemberRow[] } | YardMemberRow[]>(
    `/api/stock/yards/${yardId}/members`
  );
  return Array.isArray(response) ? response : response.members ?? [];
}

/**
 * Hand a yard over: promote the replacement *before* demoting the leaver, so the
 * backend's "a yard must keep at least one manager" guard never trips.
 *
 * Membership is looked up first rather than relying on a duplicate-key 409: the
 * `yard_members` table has no unique (yardId, userId) index in every database,
 * so a blind POST can quietly create a second row for someone who is already a
 * member. The 409 branch stays as a safety net for concurrent hand-overs.
 */
async function handoverYard(yardId: number, oldMemberId: number | undefined, newUserId: string) {
  const members = await listYardMembers(yardId);
  const matches = members.filter((member) => String(member.user?.id ?? member.userId) === String(newUserId));

  if (!matches.some((member) => member.yardRole === 'yard_manager')) {
    const alreadyMember = matches[0];
    if (alreadyMember) {
      await api.patch(`/api/stock/yards/${yardId}/members/${alreadyMember.id}`, { yardRole: 'yard_manager' });
    } else {
      try {
        await api.post(`/api/stock/yards/${yardId}/members`, {
          userId: Number(newUserId),
          yardRole: 'yard_manager'
        });
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 409) throw error;
      }
    }
  }

  if (oldMemberId) {
    await api.patch(`/api/stock/yards/${yardId}/members/${oldMemberId}`, { yardRole: 'member' });
  }
}

async function reassignRaci(item: ArchiveDependencyItem, replacement: ArchiveReplacement) {
  const { activityId, procedureId, role } = item.meta;
  if (!activityId || !procedureId || !role) {
    throw new Error('This RACI row is missing the activity it belongs to.');
  }

  // PUT replaces the whole raci object, so the current one must be read first —
  // otherwise the other three roles would be wiped.
  const response = await api.get<ActivityRaciResponse>(`/api/activities/${activityId}`);
  const rawRaci = response.activity?.raci ?? response.raci;
  // Runtime-verified object; the compiler only lost the value types.
  const current = rawRaci && typeof rawRaci === 'object' ? (rawRaci as Record<string, unknown>) : {};

  const slotKey = role === 'Responsible' ? 'responsiblePerson' : 'accountablePerson';
  const raci = {
    responsiblePerson: normalizeRaciSlot(current.responsiblePerson),
    accountablePerson: normalizeRaciSlot(current.accountablePerson),
    consultedPerson: normalizeRaciSlot(current.consultedPerson),
    informedPerson: normalizeRaciSlot(current.informedPerson),
    [slotKey]: { id: Number(replacement.id), type: replacement.type }
  };

  await api.put(`/api/activities/${activityId}`, { procedureId, raci });
}

/**
 * Perform the real hand-over for one dependency row.
 *
 * Assets are deliberately absent: they must go through the register's return
 * protocol (`AssetReturnForm`, which collects the receiving person, condition
 * notes, photos and the chain-of-custody signatures) and are then logged with
 * `recordEvent`.
 */
async function resolveDependency(
  category: ArchiveCategoryId,
  item: ArchiveDependencyItem,
  replacement?: ArchiveReplacement
) {
  const meta = item.meta;

  switch (category) {
    case 'business_systems': {
      if (!meta.businessSystemId || !meta.assignmentId) {
        throw new Error('This system row is missing its assignment id.');
      }
      await api.delete(`/api/business-systems/${meta.businessSystemId}/users/${meta.assignmentId}`);
      return;
    }

    case 'owned_procedures': {
      if (!meta.procedureId || !replacement) throw new Error('Choose who takes ownership of the procedure.');
      await api.patch(`/api/procedures/${meta.procedureId}/transfer-ownership`, {
        newOwnerId: Number(replacement.id)
      });
      return;
    }

    case 'manager_of': {
      if (!meta.reportUserId || !replacement) throw new Error('Choose a new manager for the direct report.');
      await api.put(`/api/users/${meta.reportUserId}`, { manager: Number(replacement.id) });
      return;
    }

    case 'raci': {
      if (!replacement) throw new Error('Choose who takes over this RACI role.');
      await reassignRaci(item, replacement);
      return;
    }

    case 'service_desk_admin': {
      if (!meta.deskId || !replacement) throw new Error('Choose a new desk administrator.');
      await api.put(`/api/service-desk/${meta.deskId}`, { teamAdminId: Number(replacement.id) });
      return;
    }

    case 'yard_manager': {
      if (!meta.yardId || !replacement) throw new Error('Choose a new yard manager.');
      await handoverYard(meta.yardId, meta.memberId, replacement.id);
      return;
    }

    default:
      throw new Error(`Unsupported dependency category: ${String(category)}`);
  }
}

/** `itemId` per category — see PersonArchiveService.recordEvent. */
function eventItemId(category: ArchiveCategoryId, item: ArchiveDependencyItem): number | undefined {
  const meta = item.meta;
  switch (category) {
    case 'assets': return meta.assetId;
    case 'business_systems': return meta.businessSystemId;
    case 'owned_procedures': return meta.procedureId;
    case 'manager_of': return meta.reportUserId;
    case 'raci': return meta.activityId;
    case 'service_desk_admin': return meta.deskId;
    case 'yard_manager': return meta.yardId;
    default: return undefined;
  }
}

export const personArchiveApi = {
  getPerson: (userId: string) => api.get<ArchivePersonResponse>(`/api/users/${userId}`, { skipErrorToast: true }),

  getDependencies: (userId: string) =>
    api.get<ArchiveDependenciesResponse>(`/api/users/${userId}/archive-dependencies`),

  getDraft: (userId: string) => api.get<ArchiveDraftResponse>(`/api/users/${userId}/archive-draft`),

  saveDraft: (userId: string, fields: { terminationDate?: string | null; terminationReason?: string | null }) =>
    api.put<ArchiveDraftResponse>(`/api/users/${userId}/archive-draft`, fields),

  cancelDraft: (userId: string) => api.delete(`/api/users/${userId}/archive-draft`),

  uploadAttachment: (userId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ArchiveDraftResponse>(`/api/users/${userId}/archive-draft/attachment`, form);
  },

  removeAttachment: (userId: string) =>
    api.delete<ArchiveDraftResponse>(`/api/users/${userId}/archive-draft/attachment`),

  recordEvent: (userId: string, payload: ArchiveEventPayload) =>
    api.post<ArchiveEventResponse>(`/api/users/${userId}/archive-draft/events`, payload),

  archivePerson: (userId: string, payload: { terminationDate?: string; terminationReason?: TerminationReason | string }) =>
    api.patch(`/api/users/${userId}/archive`, { archived: true, ...payload }),

  /**
   * Everyone the hand-over pickers can offer.
   *
   * `GET /api/users/selection` is the app's canonical "users for selection"
   * list: every active, non-archived person, with their permissions. The
   * journey previously filtered `useData().users` with a hand-written role
   * allowlist, and that list is permission-scoped — so valid people (Field
   * Worker, TeamAdmin, and anyone the caller can't "read the people page" for)
   * never appeared in any of the pickers.
   */
  listSelectablePeople: () =>
    api.get<ArchiveSelectablePeopleResponse>('/api/users/selection'),

  /**
   * Resolve one dependency, then log it. The event call is what fails loudly if
   * the hand-over did not actually land (409) — never swallow that.
   */
  async resolve(
    userId: string,
    category: ArchiveCategoryId,
    item: ArchiveDependencyItem,
    replacement?: ArchiveReplacement
  ): Promise<ArchiveEvent | undefined> {
    await resolveDependency(category, item, replacement);

    const itemId = eventItemId(category, item);
    if (itemId === undefined) throw new Error('This row is missing the id needed to record the change.');

    const payload: ArchiveEventPayload = {
      category,
      itemId,
      role: item.meta.role as RaciRoleLabel | undefined,
      replacementId: replacement ? Number(replacement.id) : undefined,
      replacementType: replacement ? replacement.type : undefined
    };

    const response = await personArchiveApi.recordEvent(userId, payload);
    return response.event;
  }
};
